import { useEffect, useState, useCallback } from 'react';
import { Plus, Calculator, Trash2, Edit2, AlertTriangle, X, Save } from 'lucide-react';
import { api } from '../../api/client';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useEscapeToClose } from '../../hooks/useEscapeToClose';

type Unit = {
  id: string;
  code: string;
  name: string;
  shortName: string;
  isActive: boolean;
  sortOrder: number;
  usageCount: number;
};

export function UnitsPage() {
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Unit | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Unit | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await api.get<Unit[]>('/admin/units');
      setUnits(rows);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await api.del(`/admin/units/${deleteTarget.id}`);
    refresh();
  };

  return (
    <div>
      <header style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--color-text-strong)' }}>Jedinice mere</h1>
          <div style={{ marginTop: 4, fontSize: 13, color: 'var(--color-text-muted)' }}>
            Globalne jedinice mere - važe za sve klijente. POS aplikacija samo čita ove vrednosti.
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={() => setCreating(true)} style={btnPrimary}>
          <Plus size={14} /> Nova jedinica
        </button>
      </header>

      {error && (
        <div style={errorBox}>
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      <div style={{ background: 'var(--color-bg-surface)', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--color-border)' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--color-bg)' }}>
                <th style={th}>Code</th>
                <th style={th}>Naziv</th>
                <th style={th}>Skraćenica</th>
                <th style={{ ...th, textAlign: 'right' }}>Sort</th>
                <th style={{ ...th, textAlign: 'right' }}>Koristi se u</th>
                <th style={th}>Status</th>
                <th style={{ ...th, width: 100, textAlign: 'right' }}>Akcije</th>
              </tr>
            </thead>
            <tbody>
              {!loading && units.length === 0 && (
                <tr><td colSpan={7} style={{ ...td, textAlign: 'center', padding: 40, color: 'var(--color-text-muted)' }}>
                  <Calculator size={32} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
                  <div>Nema definisanih jedinica mere</div>
                </td></tr>
              )}
              {units.map((u) => (
                <tr key={u.id}>
                  <td style={{ ...td, fontFamily: 'monospace', fontWeight: 600, color: 'var(--color-text-strong)' }}>{u.code}</td>
                  <td style={{ ...td, color: 'var(--color-text)' }}>{u.name}</td>
                  <td style={{ ...td, color: 'var(--color-text-muted)', fontFamily: 'monospace', fontSize: 12 }}>{u.shortName}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--color-text-muted)' }}>{u.sortOrder}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--color-text-muted)' }}>
                    {u.usageCount > 0 ? <strong style={{ color: 'var(--color-text)' }}>{u.usageCount}</strong> : '-'}
                  </td>
                  <td style={td}>
                    {u.isActive
                      ? <span style={badgeActive}>Aktivna</span>
                      : <span style={badgeInactive}>Neaktivna</span>}
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <button onClick={() => setEditing(u)} style={iconBtn} title="Izmeni"><Edit2 size={13} /></button>
                    <button
                      onClick={() => setDeleteTarget(u)}
                      style={{ ...iconBtn, color: u.usageCount > 0 ? 'var(--color-text-faint)' : '#fca5a5' }}
                      title={u.usageCount > 0 ? `Ne može se obrisati (koristi je ${u.usageCount} proizvoda)` : 'Obriši'}
                    >
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ marginTop: 14, padding: 12, background: 'var(--color-bg-surface)', borderRadius: 8, border: '1px solid var(--color-border)', fontSize: 12, color: 'var(--color-text-muted)' }}>
        <strong style={{ color: 'var(--color-text)' }}>Napomena:</strong> Jedinice mere su zajedničke za sve klijente platforme. POS aplikacija ih čita kroz <code style={codeStyle}>GET /api/units</code> i kešira u IndexedDB. Brisanje je dozvoljeno samo ako jedinicu ne koristi nijedan proizvod - u suprotnom je označi kao neaktivnu.
      </div>

      {creating && (
        <UnitFormDialog
          unit={null}
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); refresh(); }}
        />
      )}

      {editing && (
        <UnitFormDialog
          unit={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="Obriši jedinicu mere"
          message={
            deleteTarget.usageCount > 0
              ? `Jedinicu "${deleteTarget.code}" trenutno koristi ${deleteTarget.usageCount} proizvoda. Ne može se obrisati - deaktiviraj je umesto toga.`
              : `Da li želite da obrišete jedinicu mere "${deleteTarget.code}" (${deleteTarget.name})? Ova akcija se ne može opozvati.`
          }
          confirmLabel="Obriši"
          variant="danger"
          onConfirm={confirmDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

function UnitFormDialog({ unit, onClose, onSaved }: {
  unit: Unit | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  useEscapeToClose(onClose);
  const isEdit = !!unit;
  const [code, setCode] = useState(unit?.code || '');
  const [name, setName] = useState(unit?.name || '');
  const [shortName, setShortName] = useState(unit?.shortName || '');
  const [sortOrder, setSortOrder] = useState(unit?.sortOrder ?? 0);
  const [isActive, setIsActive] = useState(unit?.isActive ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim() || !name.trim() || !shortName.trim()) {
      setError('Code, naziv i skraćenica su obavezni');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body = { code: code.trim(), name: name.trim(), shortName: shortName.trim(), sortOrder, isActive };
      if (isEdit) {
        await api.put(`/admin/units/${unit!.id}`, body);
      } else {
        await api.post('/admin/units', body);
      }
      onSaved();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška pri čuvanju');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={dialog}>
        <header style={dialogHeader}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--color-text-strong)' }}>
            {isEdit ? `Izmena: ${unit!.code}` : 'Nova jedinica mere'}
          </h3>
          <button onClick={onClose} style={closeBtn}><X size={16} /></button>
        </header>

        <form onSubmit={handleSubmit} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label="Code (jedinstven identifikator) *">
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toLowerCase())}
              required
              placeholder="kom, kg, l, m, h"
              autoFocus
              style={{ ...input, fontFamily: 'monospace' }}
            />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14 }}>
            <Field label="Naziv *">
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Komad" style={input} />
            </Field>
            <Field label="Skraćenica *">
              <input type="text" value={shortName} onChange={(e) => setShortName(e.target.value)} required placeholder="kom" style={{ ...input, fontFamily: 'monospace' }} />
            </Field>
          </div>

          <Field label="Sort order">
            <input
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(parseInt(e.target.value, 10) || 0)}
              style={input}
            />
          </Field>

          <Field label="">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} style={{ accentColor: 'var(--color-primary)' }} />
              <span style={{ fontSize: 13, color: 'var(--color-text)' }}>Aktivna (POS može da je koristi za nove proizvode)</span>
            </label>
          </Field>

          {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16, width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
            <button type="button" onClick={onClose} style={{ ...btnSecondary, width: '100%', textAlign: 'center' }}>Otkaži</button>
            <button type="submit" disabled={saving} style={{ ...btnPrimary, width: '100%', justifyContent: 'center', opacity: saving ? 0.5 : 1 }}>
              <Save size={14} /> {saving ? 'Čuvanje...' : 'Sačuvaj'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      {label && <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4, fontWeight: 500 }}>{label}</label>}
      {children}
    </div>
  );
}

const th: React.CSSProperties = { padding: '12px 14px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '1px solid var(--color-border)' };
const td: React.CSSProperties = { padding: '12px 14px', fontSize: 13, color: 'var(--color-text)', borderBottom: '1px solid var(--color-border)' };
const btnPrimary: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnSecondary: React.CSSProperties = { padding: '9px 16px', borderRadius: 6, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const iconBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', padding: 6, marginLeft: 4, borderRadius: 4, cursor: 'pointer' };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 16 };
const overlay: React.CSSProperties = { position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)' };
const dialog: React.CSSProperties = { width: '100%', maxWidth: 480, background: 'var(--color-bg-surface)', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.5)', border: '1px solid var(--color-border)' };
const dialogHeader: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--color-border)' };
const closeBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', padding: 4 };
const input: React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'var(--color-bg)', color: 'var(--color-text-strong)', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, outline: 'none' };
const badgeActive: React.CSSProperties = { fontSize: 10, padding: '2px 8px', borderRadius: 8, background: 'rgba(16,185,129,0.15)', color: '#6ee7b7', fontWeight: 700 };
const badgeInactive: React.CSSProperties = { fontSize: 10, padding: '2px 8px', borderRadius: 8, background: 'rgba(100,116,139,0.2)', color: '#94a3b8', fontWeight: 700 };
const codeStyle: React.CSSProperties = { fontFamily: 'monospace', padding: '1px 5px', background: 'var(--color-bg)', borderRadius: 3, fontSize: 11 };
